import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { useRegion } from "@/contexts/RegionContext";
import { getAdSenseClientId, getAdSenseSlotId } from "@/lib/adsense-config";
import { AdLocation, Advertisement } from "@/types/advertisement";
import { AdvertisementSchema } from "@shared/api-schemas";
import { getJson } from "@shared/http";
import { Crown } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import { AdSenseAd } from "./AdSenseAd";

interface AdBannerProps {
  size: "header" | "sidebar" | "inline";
  className?: string;
  tags?: string[]; // Article tags for targeted advertising
  lazyLoad?: boolean; // Enable lazy loading for LCP optimization
}

// Map size to ad location
const sizeToLocation: Record<string, AdLocation> = {
  header: "homepage_top",
  sidebar: "sidebar",
  inline: "article_bottom",
};

export function AdBanner({
  size,
  className = "",
  tags = [],
  lazyLoad = false,
}: AdBannerProps) {
  const { userProfile, isAuthenticated } = useAuth();
  const { selectedRegion } = useRegion();
  const [ad, setAd] = useState<Advertisement | null>(null);
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(!lazyLoad);
  const adContainerRef = useRef<HTMLDivElement>(null);

  // Hide ads for premium users - this is the ad-free benefit
  if (isAuthenticated && userProfile?.role === "premium") {
    return null;
  }

  const location = sizeToLocation[size];

  // Lazy loading with IntersectionObserver
  useEffect(() => {
    if (!lazyLoad || !adContainerRef.current) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: "300px", // Load 300px before entering viewport
        threshold: 0.01,
      }
    );

    observer.observe(adContainerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [lazyLoad]);

  useEffect(() => {
    if (isVisible) {
      fetchAd();
    }
  }, [location, selectedRegion, isVisible]);

  const fetchAd = async () => {
    try {
      setLoading(true);

      // Build query params
      const params = new URLSearchParams();
      params.append("location", location);
      if (tags.length > 0) {
        params.append("tags", tags.join(","));
      }
      if (selectedRegion && selectedRegion !== "ALL") {
        params.append("region", selectedRegion);
      }

      const selectedAd = await getJson(
        `/api/ads/serve?${params.toString()}`,
        {},
        AdvertisementSchema.nullable()
      );

      // When no ad is available, backend returns null — hide the banner gracefully
      if (!selectedAd) {
        setAd(null);
        return;
      }

      // Map Zod schema to Advertisement type
      setAd(selectedAd as any);
    } catch (error) {
      console.error("Error fetching ad:", error);
      setAd(null);
    } finally {
      setLoading(false);
    }
  };

  const trackClick = () => {
    if (!ad) return;
    void fetch(`/api/ads/click?id=${encodeURIComponent(ad.id)}`, { keepalive: true })
      .catch((error) => console.error("Error tracking ad click:", error));
  };

  const handleAdClick = () => {
    trackClick();
    if (ad?.link) {
      window.open(ad.link, "_blank", "noopener,noreferrer");
    }
  };

  const dimensions = {
    header: "h-24 md:h-20",
    sidebar: "h-64",
    inline: "h-52",
  }[size];

  // Placeholder before lazy load
  if (!isVisible) {
    return (
      <div
        ref={adContainerRef}
        className={`${dimensions} ${className} flex items-center justify-center bg-muted/30 border border-dashed border-muted-foreground/20 rounded-md`}
        data-testid="ad-banner-placeholder"
      >
        <div className="text-muted-foreground text-xs">광고</div>
      </div>
    );
  }

  if (loading) {
    return (
      <Card
        className={`${dimensions} ${className} flex items-center justify-center bg-muted/50 border-dashed`}
      >
        <div className="animate-pulse text-muted-foreground text-sm">
          광고 로딩 중...
        </div>
      </Card>
    );
  }

  // If there's an actual ad, show it
  if (ad) {
    return (
      <Card
        className={`${dimensions} ${className} overflow-hidden hover-elevate active-elevate-2 cursor-pointer`}
        onClick={handleAdClick}
        data-testid="ad-banner-real"
      >
        <CardContent className="p-0 h-full relative">
          {ad.mobileImageUrl ? (
            <picture>
              <source media="(max-width: 768px)" srcSet={ad.mobileImageUrl} />
              <img
                src={ad.imageUrl}
                alt={ad.advertiser}
                className="w-full h-full object-cover"
              />
            </picture>
          ) : (
            <img
              src={ad.imageUrl}
              alt={ad.advertiser}
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute top-2 right-2 bg-background/80 backdrop-blur-sm px-2 py-1 rounded text-xs text-muted-foreground">
            광고
          </div>
        </CardContent>
      </Card>
    );
  }

  // No direct ad available - try AdSense
  const publisherId = getAdSenseClientId();

  const adSlot = getAdSenseSlotId(location);

  if (publisherId && adSlot) {
    // Show AdSense only when both the client and a console-issued numeric slot exist.

    return (
      <div
        className={`${dimensions} ${className}`}
        data-testid="ad-banner-adsense"
      >
        <AdSenseAd
          slot={adSlot}
          format="auto"
          responsive={true}
          className="h-full"
        />
      </div>
    );
  }

  // Final fallback: Show upgrade CTA if no ads are available
  return (
    <Card
      className={`${dimensions} ${className} flex items-center justify-center bg-muted/50 border-dashed`}
      data-testid="ad-banner"
    >
      <CardContent className="text-center py-4">
        <p className="text-sm font-medium text-muted-foreground mb-2">
          광고 영역
        </p>

        {!isAuthenticated ? (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              광고 없이 이용하려면 회원가입하세요
            </p>
            <Link href="/profile">
              <Button size="sm" variant="outline">
                회원가입
              </Button>
            </Link>
          </div>
        ) : userProfile?.role === "free" ? (
          <div className="space-y-2">
            <Crown className="h-6 w-6 text-yellow-500 mx-auto" />
            <p className="text-xs text-muted-foreground">
              프리미엄으로 업그레이드하여
              <br />
              광고 없이 이용하세요
            </p>
            <Link href="/pricing">
              <Button size="sm" variant="outline">
                프리미엄 가입
              </Button>
            </Link>
          </div>
        ) : (
          <p className="text-xs">Advertisement Space</p>
        )}
      </CardContent>
    </Card>
  );
}
